import { useMemo } from 'react';

import { useDeletePayoutAccount, useSelectPayoutAccount } from '@givebacks/api/core';
import { PAYMENT_ACCOUNTS_DICTIONARY } from '@givebacks/utils';
import { ButtonMantine, Group, Modal, ModalProps, Stack, Text, notifications } from 'ui';

const RemoveAccount = ({
  onRemove,
  onCancel,
  accountType,
  accountId,
}: {
  onRemove: () => void;
  onCancel: () => void;
  accountType: keyof typeof PAYMENT_ACCOUNTS_DICTIONARY;
  accountId: string;
}) => {
  const { mutate: deleteAccount } = useDeletePayoutAccount();

  const handleRemove = () => {
    deleteAccount(
      { accountType: accountType, uuid: accountId },
      {
        onSuccess: () => {
          notifications.show({
            title: 'Account Removed',
            message: 'Your account has been removed',
            variant: 'success',
          });
          onRemove();
        },
        onError: () => {
          notifications.show({
            title: 'Error',
            message: 'There was an error removing your account',
            variant: 'error',
          });
        },
      }
    );
  };

  const { data: paymentAccount } = useSelectPayoutAccount(accountType);

  const name = useMemo(() => {
    switch (accountType) {
      case 'bank':
        return `${paymentAccount?.display} (${paymentAccount?.bank_account_purpose})`;
      case 'paypal':
      case 'venmo':
        return `@ ${paymentAccount?.display}`;

      default:
        return '';
    }
  }, [accountType, paymentAccount?.bank_account_purpose, paymentAccount?.display]);

  return (
    <Stack gap="xl">
      <Stack gap={'xs'}>
        <Text fw={600}>Are you sure you want to remove your {accountType} account?</Text>
        <Text fz={'14px'} c="gray.6">
          {name}
        </Text>
      </Stack>
      <Group gap="xs" grow>
        <ButtonMantine variant="subtle" onClick={onCancel}>
          Cancel
        </ButtonMantine>
        <ButtonMantine onClick={handleRemove} color="cerise">
          Remove Account
        </ButtonMantine>
      </Group>
    </Stack>
  );
};

interface RemoveAccountModalProps extends ModalProps {
  modalProps: Partial<ModalProps>;
  onRemove: () => void;
  onCancel: () => void;
  accountType?: keyof typeof PAYMENT_ACCOUNTS_DICTIONARY;
  accountId?: string;
}
const RemoveAccountModal = ({
  opened,
  onClose,
  title,
  modalProps,
  onCancel,
  accountType,
  accountId,
}: RemoveAccountModalProps) => {
  return (
    <Modal opened={opened} onClose={onClose} title={title ?? 'Remove Account'} {...modalProps}>
      {accountType && accountId ? (
        <RemoveAccount
          onCancel={onCancel}
          onRemove={onClose}
          accountId={accountId}
          accountType={accountType}
        />
      ) : null}
    </Modal>
  );
};

export { RemoveAccountModal };
